import {
  Card,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { sentenceCase } from "change-case";
import FormField from "components/FormField/FormField";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { AddJobPhaseCostsInput, Plant } from "generated/graphql";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import { CustomJobPhaseCostItem } from "hooks/jobs/job-phases/useGetJobPhaseCostItem";
import PlantOptions from "modules/plants/PlantOptions";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { mapPlantProductTypeOptions } from "constants/PlantProductTypes";
import { isNilOrEmpty } from "ramda-adjunct";
import { Delete } from "@mui/icons-material";

export default function MaterialSection({
  jobPhase,
  costItem,
}: {
  jobPhase: CustomJobPhase;
  costItem: CustomJobPhaseCostItem;
}) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddJobPhaseCostsInput>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "plants",
    keyName: "key",
  });

  const plants = watch("plants");

  const totalCost =
    plants?.reduce((sum, plant) => {
      const quantity = parseFloat(plant?.quantity?.toString() || "0");
      const rate = parseFloat(plant?.rate?.toString() || "0");
      return sum + quantity * rate;
    }, 0) ?? 0;

  const handleProductChange = (
    index: number,
    value: any,
    fieldOnChange: (value: any) => void,
    availablePlants: Plant[] | undefined
  ) => {
    fieldOnChange(value);
    const currentPlantId = watch(`plants.${index}.plantId`);
    const plant = availablePlants?.find((p) => p.id === currentPlantId);
    const product = plant?.products?.find((p) => p.id === value);

    if (!isNilOrEmpty(product?.rate)) {
      setValue(`plants.${index}.rate`, product!.rate!.toString());
    }
  };

  return (
    <Card className="tour-add-material">
      <MDBox sx={{ p: 4, overflow: "auto", maxWidth: "100%" }}>
        <MDTypography variant="h6" mb={2}>
          Job Costs - {jobPhase?.organizationProductTypeName}
        </MDTypography>
        {errors.plants?.message && (
          <MDBox my={2}>
            <FormHelperText error>{errors.plants.message}</FormHelperText>
          </MDBox>
        )}
        <PlantOptions>
          {({ plantOptions, loading, plantProductOptions, search, plants: availablePlants }) => (
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell sx={{ minWidth: "200px", width: "25%" }}>Plant</TableCell>
                  <TableCell sx={{ minWidth: "200px", width: "25%" }}>Material</TableCell>
                  <TableCell sx={{ minWidth: "150px", width: "15%" }} align="right">
                    Quantity
                  </TableCell>
                  <TableCell sx={{ minWidth: "150px", width: "15%" }} align="right">
                    Rate
                  </TableCell>
                  <TableCell sx={{ minWidth: "150px", width: "15%" }} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ minWidth: "50px", width: "5%" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields?.map((plant, index) => {
                  const watchedPlant = plants?.[index];
                  const quantity = parseFloat(watchedPlant?.quantity?.toString() || "0");
                  const rate = parseFloat(watchedPlant?.rate?.toString() || "0");
                  const rowTotal = quantity * rate;

                  return (
                    <TableRow
                      key={plant.key}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>
                        <Controller
                          name={`plants.${index}.plantId`}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <AutoComplete
                              options={plantOptions}
                              field={field}
                              loading={loading}
                              error={(error?.message ?? "") as string}
                              search={search}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Controller
                          name={`plants.${index}.plantProductId`}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <AutoComplete
                              disabled={isNilOrEmpty(watch(`plants.${index}.plantId`))}
                              options={plantProductOptions[watch(`plants.${index}.plantId`)] ?? []}
                              field={{
                                ...field,
                                onChange: (value: any) =>
                                  handleProductChange(
                                    index,
                                    value,
                                    field.onChange,
                                    availablePlants
                                  ),
                              }}
                              loading={loading}
                              error={(error?.message ?? "") as string}
                              search={search}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <FormField
                          type="number"
                          inputProps={{ step: "0.01", min: 0 }}
                          placeholder="0"
                          mb={0}
                          sx={{
                            "& .MuiInputBase-root": {
                              "& input": {
                                textAlign: "right",
                              },
                            },
                          }}
                          {...control.register(`plants.${index}.quantity`)}
                          error={errors.plants?.[index]?.quantity}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <CurrencyFormField
                          control={control}
                          name={`plants.${index}.rate`}
                          label=""
                          inputProps={{ min: 0 }}
                          sendEmptyValuesAsNull={false}
                          mb={0}
                          sx={{
                            "& .MuiInputBase-root": {
                              "& input": {
                                textAlign: "right",
                              },
                            },
                          }}
                          error={errors.plants?.[index]?.rate}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <MDTypography variant="body2">{formatCentsToUSD(rowTotal)}</MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        <MDButton variant="text" color="error" onClick={() => remove(index)}>
                          <Delete />
                        </MDButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell colSpan={6}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() =>
                        append({
                          plantId: "",
                          plantProductId: "",
                          quantity: 0,
                          rate: "0",
                        })
                      }
                    >
                      Add Material
                    </MDButton>
                  </TableCell>
                </TableRow>
                {fields && fields.length > 0 && (
                  <TableRow sx={{ borderTop: 2, borderColor: "divider" }}>
                    <TableCell colSpan={4} align="right">
                      <MDTypography variant="h6">Total Material Cost:</MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="h6">{formatCentsToUSD(totalCost)}</MDTypography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </PlantOptions>
      </MDBox>
    </Card>
  );
}
